import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/libs/utils';
import { isFunction } from '@/utils/utils';

const RenderHeader = memo(({ children, column }) => {
  return isFunction(children)
    ? children(column)
    : column.header || column.dataIndex || 'N/A';
});

RenderHeader.displayName = 'RenderHeader';

function TableHeader({ columns, children }) {
  return (
    <thead className={cn('relative top-0 z-[1] border-b bg-zinc-50')}>
      <tr>
        {columns.map((column) => (
          <th
            key={String(column.dataIndex)}
            className={cn(
              'whitespace-nowrap px-4 py-2 text-left text-xs font-semibold text-gray-700'
            )}
          >
            <RenderHeader column={column}>{children}</RenderHeader>
          </th>
        ))}
      </tr>
    </thead>
  );
}

function TableCell({ value, render, row, column }) {
  return (
    <td className="px-4 py-2">
      {render ? render(value, row, column) : String(value)}
    </td>
  );
}

function TableRow({ row, columns }) {
  return (
    <tr className="whitespace-nowrap text-xs hover:bg-[rgba(var(--accentColor),0.1)] first:[&_td]:w-[100%] last:[&_td]:w-[1px]">
      {columns.map((column) => {
        const value =
          column.dataIndex === 'custom' ? row : row[column.dataIndex];

        return (
          <TableCell
            key={String(column.dataIndex)}
            value={value}
            row={row}
            column={column}
            render={
              column.render
                ? (value) => column.render(value, row, column)
                : undefined
            }
          />
        );
      })}
    </tr>
  );
}

export default function SimpleTable({
  data,
  isLoading,
  columns,
  metadata,
  rowKey = '_id',
  className,
  noDataKey = 'dataNotFound',
}) {
  const { t } = useTranslation(['messages', 'tables']);

  const columnsData = columns();

  return (
    <div
      className={cn(
        'mb-4 overflow-auto rounded-md border border-[#d9d9d9]',
        className
      )}
    >
      {isLoading ? (
        <div>{t('loading', { ns: 'tables' })}</div>
      ) : (
        <>
          {data && data?.length > 0 ? (
            <table
              className={cn(
                'w-full table-auto border-collapse bg-white text-left text-sm',
                metadata?.className
              )}
            >
              <TableHeader columns={columnsData} />

              <tbody className="[&_tr]:border-b last:[&_tr]:border-0">
                {data.map((row, idx) => (
                  <TableRow
                    key={row[rowKey] || `row-${idx}`}
                    row={row}
                    columns={columnsData}
                  />
                ))}
              </tbody>
            </table>
          ) : (
            <p className={cn('p-4 text-center', metadata?.noDataClassName)}>
              {t(noDataKey, { ns: 'messages' })}
            </p>
          )}
        </>
      )}
    </div>
  );
}
